"use client"

import { clx } from "@medusajs/ui"
import { ChevronLeft, ChevronRight } from "@medusajs/icons"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

const PAGE_BUTTON_BASE =
  "inline-flex items-center justify-center min-w-[2rem] h-8 px-2.5 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2"

const PAGE_BUTTON_ACTIVE = "bg-gold text-navy shadow-sm cursor-default"
const PAGE_BUTTON_INACTIVE =
  "bg-white text-navy border border-navy/20 hover:border-navy/40 hover:bg-white/80"

export function Pagination({
  page,
  totalPages,
  "data-testid": dataTestid,
}: {
  page: number
  totalPages: number
  "data-testid"?: string
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const arrayRange = (start: number, stop: number) =>
    Array.from({ length: stop - start + 1 }, (_, index) => start + index)

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) {
      return
    }

    const params = new URLSearchParams(searchParams)
    params.set("page", newPage.toString())
    router.push(`${pathname}?${params.toString()}`)
  }

  const renderPageButton = (
    p: number,
    label: string | number,
    isCurrent: boolean
  ) => {
    const shouldHideOnMobile = !isCurrent && p !== 1 && p !== totalPages

    return (
      <button
        key={p}
        type="button"
        aria-label={`Stran ${p}`}
        aria-current={isCurrent ? "page" : undefined}
        className={clx(PAGE_BUTTON_BASE, {
          [PAGE_BUTTON_ACTIVE]: isCurrent,
          [PAGE_BUTTON_INACTIVE]: !isCurrent,
          "hidden sm:inline-flex": shouldHideOnMobile,
        })}
        disabled={isCurrent}
        onClick={() => handlePageChange(p)}
      >
        {label}
      </button>
    )
  }

  const renderEllipsis = (key: string) => (
    <span
      key={key}
      aria-hidden="true"
      className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-8 px-1 text-sm text-navy/40 select-none"
    >
      …
    </span>
  )

  const renderPageButtons = () => {
    const buttons = []

    if (totalPages <= 7) {
      buttons.push(
        ...arrayRange(1, totalPages).map((p) =>
          renderPageButton(p, p, p === page)
        )
      )
    } else if (page <= 4) {
      buttons.push(
        ...arrayRange(1, 5).map((p) => renderPageButton(p, p, p === page))
      )
      buttons.push(renderEllipsis("ellipsis1"))
      buttons.push(
        renderPageButton(totalPages, totalPages, totalPages === page)
      )
    } else if (page >= totalPages - 3) {
      buttons.push(renderPageButton(1, 1, 1 === page))
      buttons.push(renderEllipsis("ellipsis2"))
      buttons.push(
        ...arrayRange(totalPages - 4, totalPages).map((p) =>
          renderPageButton(p, p, p === page)
        )
      )
    } else {
      buttons.push(renderPageButton(1, 1, 1 === page))
      buttons.push(renderEllipsis("ellipsis3"))
      buttons.push(
        ...arrayRange(page - 1, page + 1).map((p) =>
          renderPageButton(p, p, p === page)
        )
      )
      buttons.push(renderEllipsis("ellipsis4"))
      buttons.push(
        renderPageButton(totalPages, totalPages, totalPages === page)
      )
    }

    return buttons
  }

  const navButtonClass = (enabled: boolean) =>
    clx(
      "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-200",
      enabled
        ? "border-navy/20 bg-white text-navy hover:border-navy/40 hover:bg-white/80"
        : "border-transparent bg-transparent text-navy/30 cursor-not-allowed"
    )

  return (
    <div className="flex flex-col items-center w-full mt-12 gap-3">
      <p className="text-sm text-navy/60" aria-live="polite">
        Stran {page} od {totalPages}
      </p>

      {/* Desktop / tablet */}
      <nav
        className="hidden sm:flex items-center gap-2"
        aria-label="Navigacija po straneh"
        data-testid={dataTestid}
      >
        <button
          type="button"
          aria-label="Prejšnja stran"
          className={navButtonClass(page > 1)}
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
        >
          <ChevronLeft className="w-4 h-4" />
          Prejšnja
        </button>

        <div className="flex items-center gap-1.5 px-1">
          {renderPageButtons()}
        </div>

        <button
          type="button"
          aria-label="Naslednja stran"
          className={navButtonClass(page < totalPages)}
          disabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
        >
          Naslednja
          <ChevronRight className="w-4 h-4" />
        </button>
      </nav>

      {/* Mobile */}
      <nav
        className="flex sm:hidden items-center gap-2 w-full max-w-xs"
        aria-label="Navigacija po straneh"
      >
        <button
          type="button"
          aria-label="Prejšnja stran"
          className={clx(navButtonClass(page > 1), "flex-1 justify-center")}
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
        >
          <ChevronLeft className="w-4 h-4" />
          Prejšnja
        </button>

        <span
          className={clx(PAGE_BUTTON_BASE, PAGE_BUTTON_ACTIVE, "min-w-[2.5rem]")}
          aria-current="page"
        >
          {page}
        </span>

        <button
          type="button"
          aria-label="Naslednja stran"
          className={clx(navButtonClass(page < totalPages), "flex-1 justify-center")}
          disabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
        >
          Naslednja
          <ChevronRight className="w-4 h-4" />
        </button>
      </nav>
    </div>
  )
}
